import * as React from 'react';
import { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import { computed, action } from 'mobx';
import styled, { withTheme } from 'styled-components';
import { propStyles } from '@kurtosys/ksys-portal-components/build/utils/propStyles';
import Icon from './Icon';
import DownloadControls from './DownloadControls';
import Translate from './Translate';
import WatchListToggle from './WatchListToggle';
import ChangeIndicator from './ChangeIndicator';
import DocumentMenu from './DocumentMenu';
import Tooltip from './Tooltip';
import { ShareclassCardList, ShareclassCardRow } from './ShareclassCards';
import ColumnDropdown from './ColumnDropdown';
import StarRating from './StarRating';
import DynamicLinkProps from '../models/components/DynamicLinkProps';
import DynamicLink from './DynamicLink';

const ShareclassCardLabel = styled<any, any>('div')`
    ${propStyles};
`;

ShareclassCardLabel.defaultProps = {
    fontFamily: 'heading',
    fontWeight: 'medium',
};

const ShareclassCardValue = styled<any, any>('div')`
    ${propStyles};
`;

ShareclassCardValue.defaultProps = {
    display: 'flex',
    alignItems: 'center',
    fontFamily: 'body',
    fontWeight: 'regular',
};

const starIconStyleProps = {
    height: '1.2em',
    width: '1.2em',
};

const SeparatorWrapper = styled<any, any>('span')`
    ${propStyles};
    white-space: pre;
`;

@inject('dataStore')
@observer
class ShareclassCardLabelValue extends Component<any> {
    constructor(props) {
        super(props);

        if (this.props.column.groups) {
            this.setInitialGroupIndex();
        }
    }

    @action
    setInitialGroupIndex = () => {
        const selectedConfigGroupIndex = this.props.column.groups.findIndex(
            c => c.selected
        );
        const selectedGroupIndex = this.selectedGroupIndex
            ? this.selectedGroupIndex
            : selectedConfigGroupIndex > -1
            ? selectedConfigGroupIndex
            : 0;

        this.props.selectedGroupMap.set(
            this.props.columnIndex,
            selectedGroupIndex
        );
    };

    @computed
    get selectedGroupIndex() {
        return this.props.selectedGroupMap.get(this.props.columnIndex);
    }

    @computed
    get column() {
        return this.props.column.groups
            ? this.props.column.groups[this.selectedGroupIndex]
            : this.props.column;
    }

    render() {
        const { theme, dataStore, setSelectedGroup, shareClass } = this.props;
        const { column, selectedGroupIndex } = this;
        const dynamicLinkProps: DynamicLinkProps = {
            dataStore,
            config: column,
            shareClass: shareClass.shareClass,
            showLink: false,
            theme: theme.ShareclassColumnLink,
        };

        if (['group', 'dropdown'].includes(column.type)) {
            dynamicLinkProps.showLink = true;
            return (
                <Fragment>
                    {column.type === 'group' ? (
                        <ShareclassCardLabel {...theme.ShareclassCardLabel}>
                            <Translate>{column.label}</Translate>
                            <Tooltip content={column.disclaimers} />
                        </ShareclassCardLabel>
                    ) : (
                        <ColumnDropdown
                            column={column}
                            selectedGroupIndex={selectedGroupIndex}
                            setSelectedGroup={setSelectedGroup}
                            theme={{
                                ...theme,
                                ColumnDropdownWrapper: {
                                    ...theme.ColumnDropdownWrapper,
                                    ...theme.ShareclassCardRowDropdownWrapper,
                                },
                                ColumnDropdownInnerWrapper: {
                                    ...theme.ColumnDropdownInnerWrapper,
                                    ...theme.ShareclassCardRowDropdownInnerWrapper,
                                },
                                ColumnDropdownGroupLabel: {
                                    ...theme.ColumnDropdownGroupLabel,
                                    ...theme.ShareclassCardRowDropdownGroupLabel,
                                },
                                ColumnDropdownChevron: {
                                    ...theme.ColumnDropdownChevron,
                                    ...theme.ShareclassCardRowDropdownChevron,
                                },
                                ColumnDropdownMenu: {
                                    ...theme.ColumnDropdownMenu,
                                    ...theme.ShareclassCardRowDropdownMenu,
                                },
                                ColumnDropdownList: {
                                    ...theme.ColumnDropdownList,
                                    ...theme.ShareclassCardRowDropdownList,
                                },
                                ColumnDropdownListItem: {
                                    ...theme.ColumnDropdownListItem,
                                    ...theme.ShareclassCardRowDropdownListItem,
                                },
                            }}
                        />
                    )}
                    <ShareclassCardList flex="1 0 100%" paddingX="0">
                        {column.values.map((c, cIndex) => (
                            <ShareclassCardRow
                                key={`${c.label}-${cIndex}`}
                                paddingY="sm"
                            >
                                {c.multiple ? (
                                    <Fragment>
                                        <ShareclassCardLabel
                                            paddingX="md"
                                            {...theme.ShareclassCardLabel}
                                            {...theme.ShareclassCardLabelNested}
                                        >
                                            <Translate>{c.label}</Translate>
                                            <Tooltip content={c.disclaimers} />
                                        </ShareclassCardLabel>
                                        <ShareclassCardValue
                                            {...theme.ShareclassCardValue}
                                            {...theme.ShareclassCardValueNested}
                                        >
                                            <DynamicLink {...dynamicLinkProps}>
                                                {c.data.map((d, i) => (
                                                    <Fragment
                                                        key={`${d.code}-${i}`}
                                                    >
                                                        {d.showChange ? (
                                                            <ChangeIndicator
                                                                dataStore={
                                                                    dataStore
                                                                }
                                                                showChange={
                                                                    d.showChange
                                                                }
                                                                value={d.value}
                                                                separator={
                                                                    d.separator
                                                                }
                                                            />
                                                        ) : (
                                                            <Fragment>
                                                                <Translate>
                                                                    {d.value}
                                                                </Translate>
                                                                <SeparatorWrapper>
                                                                    {
                                                                        d.separator
                                                                    }
                                                                </SeparatorWrapper>
                                                            </Fragment>
                                                        )}
                                                    </Fragment>
                                                ))}
                                            </DynamicLink>
                                        </ShareclassCardValue>
                                    </Fragment>
                                ) : (
                                    <Fragment>
                                        <ShareclassCardLabel
                                            paddingX="md"
                                            {...theme.ShareclassCardLabel}
                                            {...theme.ShareclassCardLabelNested}
                                        >
                                            <Translate>{c.label}</Translate>
                                            <Tooltip content={c.disclaimers} />
                                        </ShareclassCardLabel>
                                        <ShareclassCardValue
                                            {...theme.ShareclassCardValue}
                                            {...theme.ShareclassCardValueNested}
                                        >
                                            <DynamicLink {...dynamicLinkProps}>
                                                {c.data.showChange ? (
                                                    <ChangeIndicator
                                                        dataStore={dataStore}
                                                        showChange={
                                                            c.data.showChange
                                                        }
                                                        value={c.data.value}
                                                    />
                                                ) : (
                                                    <Translate>
                                                        {c.data.value}
                                                    </Translate>
                                                )}
                                            </DynamicLink>
                                        </ShareclassCardValue>
                                    </Fragment>
                                )}
                            </ShareclassCardRow>
                        ))}
                    </ShareclassCardList>
                </Fragment>
            );
        }

        if (column.type === 'download') {
            return (
                <Fragment>
                    <ShareclassCardLabel {...theme.ShareclassCardLabel}>
                        <Translate>{column.label}</Translate>
                        <Tooltip content={column.disclaimers} />
                    </ShareclassCardLabel>
                    <ShareclassCardValue>
                        <DownloadControls
                            count={shareClass.downloadCount}
                            theme={{
                                ...theme,
                                ...{
                                    DownloadButton:
                                        theme.ShareclassCardDownloadButton,
                                    DownloadButtonIcon:
                                        theme.ShareclassCardDownloadButtonIcon,
                                    DownloadCount:
                                        theme.ShareclassCardDownloadCount,
                                },
                            }}
                            onClick={e => {
                                e.stopPropagation();
                                dataStore.downloadDocuments(
                                    shareClass.shareClassDownloads
                                );
                            }}
                        />
                        <Icon
                            fileName={
                                shareClass.expanded
                                    ? 'DocumentDropdownIconExpanded'
                                    : 'DocumentDropdownIcon'
                            }
                            height="1.4em"
                            width="1.4em"
                            {...theme.ShareclassCardDownloadIcon}
                        />
                    </ShareclassCardValue>
                </Fragment>
            );
        }

        if (column.type === 'watchlist' && dataStore.watchListEnabled) {
            return (
                <Fragment>
                    <ShareclassCardLabel {...theme.ShareclassCardLabel}>
                        <Translate>{column.label}</Translate>
                    </ShareclassCardLabel>
                    <WatchListToggle
                        onClick={e => {
                            e.stopPropagation();
                            shareClass.toggleWatchList();
                        }}
                        active={shareClass.inWatchList}
                        fontSize="1em"
                    />
                </Fragment>
            );
        }

        if (column.type === 'document') {
            return (
                <Fragment>
                    {Array.isArray(column.value) && column.value.length > 0 ? (
                        <DocumentMenu documents={column.value}>
                            <ShareclassCardLabel {...theme.ShareclassCardLabel}>
                                <Translate>{column.label}</Translate>
                                <Tooltip content={column.disclaimers} />
                            </ShareclassCardLabel>
                        </DocumentMenu>
                    ) : (
                        <Fragment>
                            <ShareclassCardLabel {...theme.ShareclassCardLabel}>
                                <Translate>{column.label}</Translate>
                                <Tooltip content={column.disclaimers} />
                            </ShareclassCardLabel>
                            <ShareclassCardValue {...theme.ShareclassCardValue}>
                                <Translate>
                                    {dataStore.config.noDataPlaceholder}
                                </Translate>
                            </ShareclassCardValue>
                        </Fragment>
                    )}
                </Fragment>
            );
        }

        if (column.type === 'prices') {
            return (
                <Fragment>
                    <ShareclassCardLabel {...theme.ShareclassCardLabel}>
                        <Translate>{column.label}</Translate>
                        <Tooltip content={column.disclaimers} />
                    </ShareclassCardLabel>
                    {column.data.performanceCheck &&
                    !shareClass.hasPerformance ? (
                        <Translate>{column.data.value}</Translate>
                    ) : (
                        <DownloadControls
                            display="inline-flex"
                            onClick={() => shareClass.getPriceHistory(column)}
                            count={1}
                            showCount={false}
                        />
                    )}
                </Fragment>
            );
        }

        if (column.type === 'stars' || column.type === 'starsOutOf') {
            if (column.data.performanceCheck && !shareClass.hasPerformance) {
                return <Translate>{column.data.value}</Translate>;
            }
            return (
                <Fragment>
                    <ShareclassCardLabel {...theme.ShareclassCardLabel}>
                        <Translate>{column.label}</Translate>
                        <Tooltip content={column.disclaimers} />
                    </ShareclassCardLabel>
                    <StarRating
                        rating={column.data.value}
                        noDataPlaceholder={column.noDataPlaceholder}
                        showEmptyStars={column.type === 'starsOutOf'}
                        display="block"
                        theme={{
                            ...theme,
                            ...theme.StarRatingColumnWrapper,
                            StarIcon: {
                                ...starIconStyleProps,
                                ...theme.StarIconColumn,
                            },
                            StarOutlineIcon: {
                                ...starIconStyleProps,
                                ...theme.StarOutlineIconColumn,
                            },
                        }}
                    />
                </Fragment>
            );
        }

        if (column.multiple) {
            dynamicLinkProps.showLink = true;
            return (
                <Fragment>
                    <ShareclassCardLabel {...theme.ShareclassCardLabel}>
                        <Translate>{column.label}</Translate>
                        <Tooltip content={column.disclaimers} />
                    </ShareclassCardLabel>
                    <ShareclassCardValue {...theme.ShareclassCardValue}>
                        <DynamicLink {...dynamicLinkProps}>
                            {column.data.map((d, i) => (
                                <Fragment key={`${d.code}-${i}`}>
                                    {d.showChange ? (
                                        <ChangeIndicator
                                            dataStore={dataStore}
                                            showChange={d.showChange}
                                            value={d.value}
                                            separator={d.separator}
                                        />
                                    ) : (
                                        <Fragment>
                                            <Translate>{d.value}</Translate>
                                            <SeparatorWrapper>
                                                {d.separator}
                                            </SeparatorWrapper>
                                        </Fragment>
                                    )}
                                </Fragment>
                            ))}
                        </DynamicLink>
                    </ShareclassCardValue>
                </Fragment>
            );
        }

        dynamicLinkProps.showLink = true;
        return (
            <Fragment>
                <ShareclassCardLabel {...theme.ShareclassCardLabel}>
                    <Translate>{column.label}</Translate>
                    <Tooltip content={column.disclaimers} />
                </ShareclassCardLabel>
                <ShareclassCardValue {...theme.ShareclassCardValue}>
                    <DynamicLink {...dynamicLinkProps}>
                        {column.data.showChange ? (
                            <ChangeIndicator
                                dataStore={dataStore}
                                showChange={column.data.showChange}
                                value={column.data.value}
                            />
                        ) : (
                            <Translate>{column.data.value}</Translate>
                        )}
                    </DynamicLink>
                </ShareclassCardValue>
            </Fragment>
        );
    }
}

export default withTheme(ShareclassCardLabelValue) as any;



// WEBPACK FOOTER //
// ./src/Components/ShareclassCardLabelValue.tsx